import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  Search, MapPin, Leaf, Wind, Brain, Hand, Sparkles, ArrowRight, Star, Calendar, 
  Shield, Quote, Target, Mic, Heart, Lock, CreditCard, ShieldCheck, Globe, 
  Users, Video, CheckCircle, Clock, MessageCircle, Apple
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTY_ICONS = {
  therapeute: Heart,
  naturopathe: Leaf,
  sophrologue: Wind,
  psychologue: Brain,
  osteopathe: Hand,
  hypnotherapeute: Sparkles,
  coach: Target,
  orthophoniste: Mic,
  nutritionniste: Apple,
};

// Citations philosophiques pour les praticiens en vedette
const PRACTITIONER_QUOTES = [
  "Accompagner chacun avec bienveillance, en respectant la singularité de votre parcours.",
  "Créer un espace de confiance où vous pouvez vous exprimer librement.",
  "Vous aider à retrouver l'équilibre entre corps et esprit."
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, getTestimonials } = useLanguage();
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [city, setCity] = useState('');
  const [featuredPractitioners, setFeaturedPractitioners] = useState([]);

  const testimonials = getTestimonials();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [specResponse, practResponse] = await Promise.all([
          axios.get(`${API}/specialties`),
          axios.get(`${API}/practitioners`)
        ]);
        setSpecialties(specResponse.data);
        setFeaturedPractitioners(practResponse.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (city) params.set('city', city);
    navigate(`/search?${params.toString()}`);
  };

  const getSpecialtyName = (id) => {
    return t(`specialties.${id}`) || id;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section - Emotional Promise */}
      <section className="hero-pattern relative overflow-hidden" data-testid="hero-section">
        <div className="container-app py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage-100 rounded-full">
                <Globe className="w-4 h-4 text-sage-600" />
                <span className="text-sm font-medium text-sage-700">{t('landing.badge') || 'Europe de l\'Est & Expatriés'}</span>
              </div>
              
              {/* Emotional Title - Reduced size */}
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight">
                {t('landing.heroTitle') || 'Se sentir'} <span className="text-sage-500">{t('landing.heroTitleHighlight') || 'compris'}</span>{t('landing.heroTitleEnd') || ', dans sa langue et sa culture'}
              </h1>
              
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                {t('landing.heroSubtitle') || 'Le soutien bien-être qui vous ressemble, où que vous soyez en Europe. Trouvez un praticien qui comprend votre histoire et votre contexte culturel.'}
              </p>

              {/* Search Form */}
              <form onSubmit={handleSearch} className="bg-white p-4 md:p-6 rounded-2xl shadow-soft-lg border border-sand-200" data-testid="search-form">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('landing.searchSpecialty') || 'Spécialité'}</label>
                    <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                      <SelectTrigger className="w-full h-12 rounded-xl border-sand-200" data-testid="specialty-select">
                        <SelectValue placeholder={t('landing.searchSpecialtyPlaceholder') || 'Choisir une spécialité'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search.allSpecialties') || 'Toutes les spécialités'}</SelectItem>
                        {specialties.map((spec) => (
                          <SelectItem key={spec.id} value={spec.id}>{getSpecialtyName(spec.id)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('landing.searchCity') || 'Ville'}</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input 
                        type="text"
                        placeholder="Votre ville"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-sand-200"
                        data-testid="city-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-end">
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-sage-500 hover:bg-sage-600 text-white rounded-xl font-medium"
                      data-testid="search-button"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      Rechercher
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Right Image */}
            <div className="hidden lg:block relative animate-fadeIn stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg">
                <img 
                  src="/images/hero-bg.jpg"
                  alt="Bien-être et nature"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sage-900/20 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-soft-lg border border-sand-200 animate-fadeIn stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-terracotta-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-terracotta-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Accompagnement personnalisé</p>
                    <p className="text-xs text-muted-foreground">Dans votre langue maternelle</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Opora Vie - Cultural Specificity */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-section">
        <div className="container-app">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-title mb-4">Pourquoi Opora Vie ?</h2>
            <p className="text-lg text-muted-foreground">
              Le soin est plus efficace quand il y a une affinité culturelle
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-sand-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100 rounded-2xl flex items-center justify-center">
                <Globe className="w-8 h-8 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">
                Compréhension culturelle
              </h3>
              <p className="text-muted-foreground">
                Nos praticiens ne parlent pas seulement votre langue, ils comprennent votre histoire et votre contexte culturel.
              </p>
            </div>
            
            <div className="bg-sand-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-terracotta-100 rounded-2xl flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-terracotta-500" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">
                Communication authentique
              </h3>
              <p className="text-muted-foreground">
                Exprimez-vous dans votre langue maternelle pour un accompagnement plus profond et plus efficace.
              </p>
            </div>
            
            <div className="bg-sand-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">
                Communauté bienveillante
              </h3>
              <p className="text-muted-foreground">
                Rejoignez une communauté de praticiens et de clients qui partagent les mêmes valeurs et références.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Practitioners with Quotes */}
      {featuredPractitioners.length > 0 && (
        <section className="py-16 md:py-24 bg-sand-100" data-testid="featured-practitioners">
          <div className="container-app">
            <div className="text-center mb-12">
              <h2 className="section-title mb-4">Nos praticiens à l'écoute</h2>
              <p className="section-subtitle mx-auto">
                Des professionnels passionnés qui vous accompagnent avec bienveillance
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPractitioners.map((practitioner, index) => {
                const IconComponent = SPECIALTY_ICONS[practitioner.specialty] || Leaf;
                const quote = PRACTITIONER_QUOTES[index % PRACTITIONER_QUOTES.length];
                return (
                  <Link
                    key={practitioner.id}
                    to={`/practitioner/${practitioner.id}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-lg transition-all duration-300 group"
                    data-testid={`practitioner-card-${practitioner.id}`}
                  >
                    <div className="aspect-[4/3] relative">
                      <img 
                        src={practitioner.photo_url 
                          ? (practitioner.photo_url.startsWith('/api/photos') || practitioner.photo_url.startsWith('/uploads')
                              ? `${process.env.REACT_APP_BACKEND_URL}${practitioner.photo_url}`
                              : practitioner.photo_url)
                          : `/images/default-practitioner.jpg`
                        }
                        alt={`${practitioner.user?.first_name} ${practitioner.user?.last_name}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="badge-specialty">
                          <IconComponent className="w-3 h-3 mr-1" />
                          {getSpecialtyName(practitioner.specialty)}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                        {practitioner.user?.first_name} {practitioner.user?.last_name}
                      </h3>
                      
                      {/* Quote */}
                      <p className="text-sm text-muted-foreground italic mb-4 leading-relaxed">
                        "{quote}"
                      </p>
                      
                      <div className="flex items-center justify-between">
                        {practitioner.city && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {practitioner.city}
                          </p>
                        )}
                        {practitioner.price && (
                          <p className="text-sage-600 font-medium">
                            {practitioner.price}€
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/search">
                <Button variant="outline" className="rounded-full px-8" data-testid="view-all-practitioners">
                  Voir tous les praticiens
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white" data-testid="how-it-works">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">Comment ça marche ?</h2>
            <p className="section-subtitle mx-auto">
              Prenez rendez-vous en quelques clics. Simple, sécurisé, serein.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <h3 className="font-heading text-lg font-medium text-foreground mb-2">Trouvez</h3>
              <p className="text-sm text-muted-foreground">
                Recherchez un praticien par spécialité, langue ou localisation
              </p>
              <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-sand-200" />
            </div>
            
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <h3 className="font-heading text-lg font-medium text-foreground mb-2">Réservez</h3>
              <p className="text-sm text-muted-foreground">
                Choisissez un créneau disponible selon vos préférences
              </p>
              <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-sand-200" />
            </div>
            
            <div className="text-center relative">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <h3 className="font-heading text-lg font-medium text-foreground mb-2">Payez</h3>
              <p className="text-sm text-muted-foreground">
                Paiement sécurisé en ligne, votre RDV est confirmé instantanément
              </p>
              <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-sand-200" />
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <h3 className="font-heading text-lg font-medium text-foreground mb-2">Consultez</h3>
              <p className="text-sm text-muted-foreground">
                En cabinet ou en visio, cliquez et commencez votre séance
              </p>
            </div>
          </div>

          {/* Security Reassurance */}
          <div className="mt-16 bg-sand-50 rounded-2xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-sage-100 rounded-2xl flex items-center justify-center">
                  <Shield className="w-10 h-10 text-sage-600" />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                  Un espace sécurisé et privé
                </h3>
                <p className="text-muted-foreground">
                  Votre lien de connexion est généré automatiquement. Vous n'avez rien à installer : 
                  cliquez, et commencez votre séance en toute sérénité. Vos données sont protégées 
                  conformément au RGPD et vos paiements sont sécurisés par Stripe.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-16 md:py-24 bg-sand-100" data-testid="specialties-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">Nos spécialités</h2>
            <p className="section-subtitle mx-auto">
              Un large éventail de pratiques pour votre bien-être
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {specialties.map((spec, index) => {
              const IconComponent = SPECIALTY_ICONS[spec.id] || Leaf;
              return (
                <div
                  key={spec.id}
                  onClick={() => navigate(`/search?specialty=${spec.id}`)}
                  className="specialty-card text-center cursor-pointer"
                  data-testid={`specialty-${spec.id}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-sage-600" />
                  </div>
                  <h3 className="font-heading font-medium text-foreground">{getSpecialtyName(spec.id)}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="testimonials-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">Ce qu'ils disent de nous</h2>
            <p className="section-subtitle mx-auto">
              Témoignages de notre communauté
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-sand-50 rounded-2xl p-6 md:p-8 relative"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="w-10 h-10 text-sage-200 absolute top-6 right-6" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-terracotta-400 text-terracotta-400" />
                  ))}
                </div>
                <p className="text-foreground mb-6 leading-relaxed relative z-10">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-sage-200 rounded-full flex items-center justify-center">
                    <span className="font-heading font-semibold text-sage-700">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section for Practitioners */}
      <section className="py-16 md:py-24 bg-sage-500" data-testid="cta-section">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Vous êtes praticien ?
          </h2>
          <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
            Rejoignez notre communauté de praticiens et développez votre activité 
            auprès d'une clientèle qui partage votre culture et vos valeurs.
          </p>
          <Link to="/auth?tab=register&role=practitioner">
            <Button className="bg-white text-sage-600 hover:bg-sand-100 rounded-full px-8 py-6 text-lg font-medium shadow-lg" data-testid="cta-practitioner">
              Devenir praticien
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-12 bg-sand-50 border-t border-sand-200">
        <div className="container-app">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Conforme RGPD</p>
                <p className="text-xs">Vos données sont protégées</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Paiements sécurisés</p>
                <p className="text-xs">Powered by Stripe</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <Video className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Visio intégrée</p>
                <p className="text-xs">Rien à installer</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Connexion SSL</p>
                <p className="text-xs">100% sécurisée</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white" data-testid="footer">
        <div className="container-app">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-sage-500 rounded-xl flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading text-xl font-semibold">Opora Vie</span>
              </div>
              <p className="text-sand-300 text-sm">
                Le soutien bien-être qui vous ressemble.
              </p>
            </div>
            
            {/* Navigation */}
            <div>
              <h4 className="font-medium mb-4">Navigation</h4>
              <ul className="space-y-2 text-sand-300 text-sm">
                <li><Link to="/search" className="hover:text-white">Trouver un praticien</Link></li>
                <li><Link to="/about" className="hover:text-white">À propos</Link></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/boutique" className="hover:text-white">Boutique</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-medium mb-4">Informations légales</h4>
              <ul className="space-y-2 text-sand-300 text-sm">
                <li><Link to="/cgu" className="hover:text-white">Conditions d'utilisation (CGU)</Link></li>
                <li><Link to="/cgv" className="hover:text-white">Conditions de vente (CGV)</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Politique de confidentialité</Link></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-medium mb-4">Contact</h4>
              <p className="text-sand-300 text-sm">
                contact@apora.net
              </p>
            </div>
          </div>
          
          <div className="border-t border-sand-700 pt-8">
            <p className="text-sand-300 text-sm text-center">
              © 2024 Opora Vie. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
